import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box } from 'src/components/Box';
import { CircularProgress } from 'src/components/CircularProgress';
import { Meta } from 'src/components/Meta';
import { useSession } from 'src/contexts/SessionContext';
import { externalLinks } from 'src/externalLinks';
import { AppFrameConfig } from 'src/shared/AppFrame/AppFrameConfig';

const REACT_EMBEDDED_QS = 'react_embedded';

export default function EmbedPage() {
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const previousPathRef = useRef<string | null>(null);
  const [iframeSrc, setIframeSrc] = useState<string | null>(null);
  const { session } = useSession();
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const pathToEmbed = params['*'];
  const lastTransitionRef = useRef<number>(0);

  const contentFrameHeight = 'calc(100svh - 56px)';

  useEffect(() => {
    const currentPathIsEmbed = location.pathname.includes('/embed');

    if (!currentPathIsEmbed || !iframeSrc) {
      const host =
        externalLinks.railsBase ||
        window.location.protocol + '//' + window.location.host;
      const urlToEmbed = new URL(host + '/' + pathToEmbed + location.search);
      urlToEmbed.searchParams.append(REACT_EMBEDDED_QS, 'true');
      setIframeSrc(urlToEmbed.href);
      setLoading(true);
    } else {
      setLoading(true);
      iframeRef.current?.contentWindow?.postMessage(
        {
          type: 'nav-changed',
          url: `/${pathToEmbed}`,
        },
        '*'
      );
    }

    previousPathRef.current = location.pathname;
  }, [iframeSrc, location, pathToEmbed]);

  useEffect(() => {
    const handleMessage = (e) => {
      if (e.data.type === 'title-changed') {
        if (e.data.title && e.data.title !== title) {
          setTitle(e.data.title);
        }
      }
      if (e.data.type === 'did-transition') {
        setLoading(false);
        if (e.data.path) {
          const now = Date.now();
          if (now - lastTransitionRef.current < 100) {
            return;
          }
          lastTransitionRef.current = now;

          const newUrl = new URL(
            window.location.protocol +
              '//' +
              window.location.host +
              `${externalLinks.base}/embed` +
              e.data.path
          );
          newUrl.searchParams.delete(REACT_EMBEDDED_QS);
          window.history.replaceState({}, '', stripTrailingSlash(newUrl.href));
        }
      }
    };

    window.addEventListener('message', handleMessage, false);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [title, setTitle, pathToEmbed, navigate]);

  return (
    <>
      <Meta title={title} brand={session?.brand} />

      <AppFrameConfig title={title} frameOnly compactTitle id="embed" />

      <Box
        sx={{
          top: '0px',
          left: '0px',
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: contentFrameHeight,
          pointerEvents: 'none',
          opacity: loading ? 100 : 0,
          transition: 'opacity 0.15s',
          width: '100%',
        }}
      >
        <CircularProgress size="large" />
      </Box>
      {iframeSrc && (
        <Box
          component="iframe"
          src={iframeSrc}
          title="description"
          ref={iframeRef}
          // Key to force iframe to be rerendered when moving between paths that are both embedded
          sx={{
            width: '100%',
            height: contentFrameHeight,
            border: 0,
            display: 'block',
            opacity: loading ? 0 : 100,
          }}
        ></Box>
      )}
    </>
  );
}

function stripTrailingSlash(str) {
  return str.endsWith('/') ? str.slice(0, -1) : str;
}
